import React, { useState, useEffect, useMemo } from 'react';
import { string, bool } from 'prop-types';
import { useProduct } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { PromoModelProvider } from '../contexts/PromoModelProvider';
import { PromoCartProvider } from '../contexts/PromoCartProvider';
import { PromoPresentationProvider } from '../contexts/PromoPresentationProvider';
import { PromoDrawer } from './promo-drawer/PromoDrawer';
import { PromoCallToAction } from './promo-call-to-action/PromoCallToAction';
import { isValidPromotionData } from '../utils/promo-model-utils';

import './PromotionProducts.scss';

const returnProductIfValid = (data) => (
  data?.product?.pricing?.promotion
    && isValidPromotionData(data.product.pricing.promotion)
    ? data.product
    : null
);

const isSanitaryAnchorItemId = (itemId) => (
  typeof itemId === 'string'
    && itemId.length === 9
    && !Number.isNaN(Number(itemId))
);

const displayPromotion = (bogoEnabled, msbEnabled, anchorItem) => {
  return (bogoEnabled && anchorItem?.pricing?.promotion?.experienceTag === 'BOGO')
        || (msbEnabled && anchorItem?.pricing?.promotion?.experienceTag === 'MSBONLINE');
};

export const PromotionProducts = ({ itemId, bogoEnabled, msbEnabled }) => {
  useEffect(() => {
    if (bogoEnabled || msbEnabled) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('promotion-products.ready');
    }
  }, [bogoEnabled, msbEnabled]);

  const { storeId, storeZip } = useStore();
  const [renderPromoDrawer, setRenderPromoDrawer] = useState(false);
  const [isBatchLoading, setIsBatchLoading] = useState(true);
  const renderDrawer = () => { setRenderPromoDrawer(true); };

  const { data } = useProduct({
    itemId,
    storeId,
    ssr: false,
    domains: ['$ProductPod'],
    skip: (!bogoEnabled && !msbEnabled) || !isSanitaryAnchorItemId(itemId),
  });
  const anchorItem = useMemo(() => returnProductIfValid(data), [data]);

  return displayPromotion(bogoEnabled, msbEnabled, anchorItem) ? (
    <PromoModelProvider
      anchorItem={anchorItem}
      storeId={storeId}
      storeZip={storeZip}
      setIsBatchLoading={setIsBatchLoading}
    >
      <PromoCartProvider>
        <PromoPresentationProvider isBatchLoading={isBatchLoading}>
          <PromoCallToAction
            onPromoCtaHover={renderDrawer}
            isInPromoDrawer={false}
          />
          {renderPromoDrawer && (
            <PromoDrawer />
          )}
        </PromoPresentationProvider>
      </PromoCartProvider>
    </PromoModelProvider>
  ) : null;
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  itemId: string,
  bogoEnabled: bool,
  msbEnabled: bool
};

PromotionProducts.defaultProps = {
  itemId: '',
  bogoEnabled: false,
  msbEnabled: false
};
